import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { isDesktop } from "react-device-detect";
import { Helmet } from "react-helmet";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { Box, TextField, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import queryString from "query-string";

import { OrganizationLogo } from "components/OrganizationLogo";
import HubspotAntiFormTracking from "components/HubspotAntiFormTracking";

import { useLoginContent, useTipsContent } from "localization/utils";

import { apiClient } from "utils/apiClient";

import { ReactComponent as EmailSentLogo } from "./email-sent.svg";
import AuthLayout from "./AuthLayout";
import { getErrorMessage } from "./helpers/authHelpers";
import { usePasswordlessLogin } from "./hooks/usePasswordlessLogin";
import {
	HeaderWithSubText,
	HeaderWithSubTextIcon,
	InputGroup,
	LoginButton,
	LoginCard,
	LoginContainer,
	SigInWrapper,
} from "./styles";
import { useAuthContext } from "../context/GameAuthContext";

export interface loginHeaderModel {
	title: string;
	content: string;
}

export interface loginFormModel {
	title: string;
	loginButton: string;
}
export interface TipsModel {
	title: string;
	content: string;
}

export type LoginPageContent = {
	headerTitle: string;
	headerContent: string;
	formTitle: string;
	formButton: string;
	tipsTitle: string;
	tipsContent: string;
};

export interface Props {
	loginContent: LoginPageContent;
	orgSlug: string;
	roomTemplateId?: string;
}

const LoginScreen: React.FC<Props> = (props: Props) => {
	const tipsContent = useTipsContent({ contentId: props?.loginContent?.tipsContent });
	return (
		<AuthLayout tips={[tipsContent]}>
			<LoginFormCard {...props} />
		</AuthLayout>
	);
};

interface LoginFormValues {
	name: string;
	email: string;
	password: string;
}

const LoginFormCard: React.FC<Props> = ({ loginContent, orgSlug, roomTemplateId }: Props) => {
	const classes = useStyles();
	const intl = useIntl();
	const location = useLocation();
	const { isLoggingIn } = usePasswordlessLogin(roomTemplateId);
	const { login } = useAuthContext();
	const loginHeaderContent = useLoginContent({ contentId: loginContent?.headerContent, orgSlug });

	const isPasswordlessRegistration = queryString.parse(location.search)?.p === "1" && roomTemplateId;

	const [isPasswordlessRegistrationSuccess, setIsPasswordlessRegistrationSuccess] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(false);
	const [error, setError] = React.useState<any>(null);

	const { register, handleSubmit, errors } = useForm<LoginFormValues>();

	const submit = async (val: LoginFormValues) => {
		setIsLoading(true);
		setError("");
		try {
			if (isPasswordlessRegistration) {
				await apiClient("auth/register/passwordless", {
					data: {
						name: val.name,
						email: val.email,
						roomTemplateId: roomTemplateId,
					},
				});
				// Inform user that an email has been sent
				setIsPasswordlessRegistrationSuccess(true);
			} else {
				await login(val.email, val.password);
			}
		} catch (err) {
			const message = getErrorMessage((err as any).json.code);
			setError(message);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<LoginContainer>
			<Helmet>
				<title>
					{intl.formatMessage({
						id: "LOGIN.page.title",
					})}
				</title>
			</Helmet>
			<LoginCard elevation={0}>
				{isDesktop && (
					<Box width={"100%"} display={"flex"} justifyContent={"center"}>
						<OrganizationLogo />
					</Box>
				)}
				<HeaderWithSubText sx={{ marginTop: 4 }}>
					{loginContent?.headerTitle && (
						<Typography variant="h5" className={classes.title}>
							<FormattedMessage
								id={loginContent?.headerTitle}
								defaultMessage={intl.formatMessage({ id: "AUTH.login.title" })}
							/>
						</Typography>
					)}
					<Typography data-testid="login_description" variant="caption" className={classes.headerContent}>
						{loginHeaderContent}
					</Typography>
				</HeaderWithSubText>
				<SigInWrapper>
					<Typography data-testid="login_title" variant="h5" className={classes.loginFormTitle}>
						<FormattedMessage
							id={loginContent?.formTitle || "AUTH.default.login-form.tile"}
							defaultMessage={intl.formatMessage({ id: "AUTH.default.login-form.tile" })}
						/>
					</Typography>
				</SigInWrapper>
				{isPasswordlessRegistrationSuccess ? (
					<HeaderWithSubTextIcon>
						<EmailSentLogo width="100" height="100" />
						<Typography
							variant="h5"
							sx={{
								fontWeight: 900,
								fontSize: 28,
								margin: 0,
								marginBottom: 4,
							}}
						>
							<FormattedMessage id="AUTH.passwordless-email.title" />
						</Typography>
						<Typography variant="caption" sx={{ fontWeight: "normal", fontSize: 14 }}>
							<FormattedMessage id="AUTH.passwordless-email.subtitle" />
						</Typography>
						<br />
						<br />
						<br />
						<Typography variant="caption" sx={{ fontWeight: "normal", fontSize: 14 }}>
							ps. <FormattedMessage id="AUTH.passwordless-email.note" />
						</Typography>
					</HeaderWithSubTextIcon>
				) : (
					<>
						<form onSubmit={handleSubmit(submit)}>
							<HubspotAntiFormTracking />
							<div style={{ display: "flex", flexDirection: "column" }}>
								{isPasswordlessRegistration && (
									<InputGroup>
										<TextField
											data-testid="name_field"
											inputRef={register({ required: true })}
											variant="outlined"
											fullWidth
											type="text"
											name="name"
											label={intl.formatMessage({ id: "AUTH.login.input.name.label" })}
											autoComplete="off"
											disabled={isLoading || isLoggingIn}
											helperText={errors.name?.message}
											size="small"
											error={!!errors.name?.type}
											sx={{
												"& .MuiFormLabel-root": {
													fontSize: "18px",
												},
												"& .MuiInputBase-root": {
													fontWeight: 500,
													fontSize: "18px",
													borderRadius: "8px",
												},
											}}
										/>
									</InputGroup>
								)}
								<InputGroup>
									<TextField
										id="field-email"
										inputRef={register({ required: true })}
										variant="outlined"
										fullWidth
										type="email"
										name="email"
										label={intl.formatMessage({ id: "AUTH.login.input.email.label" })}
										autoComplete="off"
										disabled={isLoading || isLoggingIn}
										helperText={error || errors.email?.message}
										size="small"
										error={error || !!errors.email?.type}
										sx={{
											"& .MuiFormLabel-root": {
												fontSize: "18px",
											},
											"& .MuiInputBase-root": {
												fontWeight: 500,
												fontSize: "18px",
												borderRadius: "8px",
											},
										}}
									/>
								</InputGroup>
								{!isPasswordlessRegistration && (
									<InputGroup>
										<TextField
											id="field-password"
											inputRef={register({ required: true })}
											variant="outlined"
											fullWidth
											type="password"
											name="password"
											label={intl.formatMessage({ id: "AUTH.login.input.password.label" })}
											disabled={isLoading || isLoggingIn}
											helperText={errors.password?.message}
											size="small"
											error={error || !!errors.password?.message}
											sx={{
												"& .MuiFormLabel-root": {
													fontSize: "18px",
												},
												"& .MuiInputBase-root": {
													fontWeight: 500,
													fontSize: "18px",
													borderRadius: "8px",
												},
											}}
										/>
									</InputGroup>
								)}
								<div style={{ margin: "8px 0px" }} />
								<InputGroup>
									<LoginButton
										fullWidth
										data-testid="login_button"
										type="submit"
										variant="contained"
										disabled={isLoading || isLoggingIn}
									>
										<FormattedMessage
											id={loginContent?.formButton || "AUTH.default.login-form.button"}
											defaultMessage={intl.formatMessage({
												id: "AUTH.default.login-form.button",
											})}
										/>
									</LoginButton>
								</InputGroup>
							</div>
						</form>
						<Link to={`/${orgSlug || "enboq"}/reset-password`} className={classes.forgotPassword}>
							<Typography
								data-testid="forgot_password"
								className={`${classes.forgotPassword} login-screen-forgot-password`}
							>
								{" "}
								{intl.formatMessage({ id: "AUTH.tips.forgotPassword" })}
							</Typography>
						</Link>
					</>
				)}
			</LoginCard>
		</LoginContainer>
	);
};

export default LoginScreen;

const useStyles = makeStyles((theme) => ({
	title: {
		fontWeight: 900,
		fontSize: 30,
		margin: 0,
		marginBottom: 16,

		[theme.breakpoints.down("mobile")]: {
			marginBottom: 0,
		},
	},
	headerContent: {
		fontWeight: "normal",
		fontSize: "18px",
		[theme.breakpoints.down("mobile")]: {
			fontSize: 16,
			lineHeight: "22px",
		},
	},
	loginFormTitle: {
		fontWeight: 800,
		fontSize: "32px",
		margin: 0,
		[theme.breakpoints.down("mobile")]: {
			fontSize: 16,
		},
	},
	link: {
		"&:link": {
			color: "#777",
			textDecoration: "none",
			fontSize: 15,
			fontWeight: "bold",
		},
		"&:visited": {
			color: "#777",
		},
	},
	forgotPassword: {
		textDecoration: "underline",
		textUnderlinePosition: "under",
		fontWeight: 700,
		color: "#000",
		fontSize: "18px",
	},
}));
